import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const BlogRootCanal = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="relative h-[460px] overflow-hidden">
        <img
          src="/images/27.png"
          alt="Root Canal Treatment"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-12 px-8 md:px-20">
          <div className="max-w-3xl">
            <span className="inline-block bg-blue-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">DENTAL</span>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-lg mb-4">
              Root Canal Treatment: Separating Fact from Fear
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><Calendar size={14} />12 Apr 2024</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />6 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-gray-100 py-3.5">
        <div className="max-w-7xl mx-auto px-6 text-sm flex items-center gap-2">
          <Link to="/" className="text-gray-500 hover:text-[#39a5d3]">Home</Link>
          <span className="text-gray-400">/</span>
          <Link to="/blog" className="text-gray-500 hover:text-[#39a5d3]">Blogs</Link>
          <span className="text-gray-400">/</span>
          <span className="text-[#39a5d3] font-semibold">Root Canal Treatment: Separating Fact from Fear</span>
        </div>
      </div>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Article */}
            <div className="lg:col-span-2 text-gray-700 leading-relaxed">

              <p className="text-lg leading-relaxed mb-8 border-l-4 border-[#39a5d3] pl-5 bg-blue-50 py-4 pr-4 rounded-r-lg">
                Ask most people what they fear most about going to the dentist and root canal treatment will be near the top of the list. Yet this fear is almost entirely based on outdated information, exaggerated stories, and the procedure's old reputation from decades ago when dental anaesthesia was far less effective than it is today. Modern root canal treatment is routine, virtually painless, and one of the most important tooth-saving procedures in dentistry. This article explains what the procedure actually involves, why the fear is unwarranted, and when you need one.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">What Is Root Canal Treatment?</h2>
              <p className="mb-4">Root canal treatment — also called endodontic treatment — is a procedure to remove infected or damaged tissue from inside a tooth. Each tooth contains a central chamber called the pulp, which houses nerves, blood vessels, and connective tissue. When the pulp becomes infected or inflamed — due to deep decay, a cracked tooth, repeated dental procedures, or trauma — it cannot heal on its own and must be removed.</p>
              <p className="mb-8">Without treatment, the infection spreads into the surrounding bone, causing an abscess that can be extremely painful and, in rare cases, spread to other parts of the head and neck. Root canal treatment cleans out the infected pulp, disinfects the canals, and seals them to prevent reinfection. The tooth is then restored with a crown, allowing it to function normally for many years — often for the rest of your life.</p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">The Biggest Myths — Debunked</h2>
              <div className="space-y-3 mb-10">
                {[
                  { t: 'Myth 1: Root canals are extremely painful', d: 'This is the most pervasive and most inaccurate myth. Root canal treatment is performed under local anaesthetic — the same injection used for fillings. The procedure itself is no more painful than having a tooth filled. The pain people associate with root canals is actually the pain of the infection before treatment, not the treatment itself. Most patients report that the procedure is far more comfortable than they anticipated.' },
                  { t: 'Myth 2: It is better to just pull the tooth', d: 'Extraction is almost never the better option for a restorable tooth. Losing a tooth sets off a chain of consequences — neighbouring teeth shift, the jawbone at the extraction site begins to resorb, and chewing becomes more difficult. Replacing a missing tooth with an implant or bridge costs significantly more than root canal treatment. Keeping your natural tooth is almost always the better long-term choice.' },
                  { t: 'Myth 3: Root canal treatment causes illness', d: 'This myth originated from outdated and thoroughly discredited research from the 1920s. Modern research has found no credible link between root canal treatment and systemic disease. The procedure is safe and has been performed on hundreds of millions of patients worldwide.' },
                  { t: 'Myth 4: If there is no pain, you do not need a root canal', d: 'Many teeth that require root canal treatment cause no pain at all — particularly in cases where the nerve has already died. Infection can be present and advancing on X-ray without causing any symptoms. This is one of the reasons regular dental check-ups are important.' },
                  { t: 'Myth 5: Root canals require many appointments', d: 'Most root canal treatments are completed in one or two appointments. Single-visit root canal treatment is now the standard for most cases, particularly with modern rotary instrumentation and digital X-rays. Complex cases with multiple canals or significant infection may require a second visit, but the days of multiple appointments spread over weeks are largely behind us.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="font-bold text-gray-900 mb-1.5">{item.t}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>

              {/* Mid article image */}
              <div className="my-10 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                  <img
                    src="/images/28.png"
                    alt="Dental treatment"
                    className="w-full h-64 object-cover"
                  />
                  <div className="bg-gray-50 px-4 py-2.5 border-t border-gray-100">
                    <p className="text-xs text-gray-500 italic text-center">Modern root canal treatment is a routine, comfortable procedure that saves teeth and relieves pain.</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Signs You May Need a Root Canal</h3>
                  <p className="text-sm leading-relaxed mb-3">Not all of these symptoms are always present — some people need root canal treatment with no symptoms at all. However, the following warrant prompt dental assessment:</p>
                  <ul className="space-y-2">
                    {[
                      'Severe toothache, particularly when biting or applying pressure',
                      'Prolonged sensitivity to hot or cold after the stimulus is removed',
                      'Darkening or discolouration of the tooth',
                      'A persistent pimple or bump on the gum near the tooth',
                      'Swelling or tenderness in the surrounding gum',
                      'A cracked or chipped tooth with associated pain',
                    ].map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#39a5d3] mt-1.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">What Happens During the Procedure</h2>
              <div className="space-y-4 mb-10">
                {[
                  { n: '01', t: 'Anaesthetic and Access', d: 'Local anaesthetic is administered — the same injection used for fillings. Once the area is fully numb, a small opening is made in the top of the tooth to access the pulp chamber. A rubber dam is placed around the tooth to keep the area clean and dry throughout the procedure.' },
                  { n: '02', t: 'Removing the Pulp', d: 'Using very fine instruments called files — now typically flexible nickel-titanium rotary files driven by a small motor — the dentist removes the infected or damaged pulp tissue from the canals. Each canal is carefully shaped to receive the filling material. The canals are simultaneously irrigated with disinfectant solution to remove bacteria and debris.' },
                  { n: '03', t: 'Disinfection and Shaping', d: 'This is the most critical step. The canals are meticulously cleaned and shaped using a combination of mechanical files and chemical irrigants. The goal is to eliminate all bacteria and create a canal shape that can be reliably sealed.' },
                  { n: '04', t: 'Filling the Canals', d: 'The cleaned and shaped canals are filled with a rubber-like material called gutta-percha, combined with a sealer. This fills the space completely, preventing bacteria from re-entering. An X-ray is taken to confirm the fill is complete and accurate.' },
                  { n: '05', t: 'Restoration', d: 'A temporary filling is placed at the end of the appointment. Because root-treated teeth become more brittle over time, a crown is almost always recommended to protect the tooth from fracture. The crown appointment typically takes place 2 to 4 weeks later.' },
                ].map(item => (
                  <div key={item.n} className="flex gap-4 bg-white border border-gray-200 rounded-lg p-5">
                    <div className="bg-[#19354a] text-white font-bold text-sm w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0">{item.n}</div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.t}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">After the Procedure — What to Expect</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
                {[
                  'Mild soreness for 2 to 3 days, easily managed with paracetamol or ibuprofen',
                  'The tooth may feel slightly different when biting for a few days',
                  'Avoid chewing on the treated side until the crown is placed',
                  'Any swelling should subside within a few days',
                  'Contact your dentist if severe pain, swelling, or discharge develops',
                  'Attend the follow-up appointment promptly to have the crown placed',
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <span className="w-2 h-2 rounded-full bg-[#39a5d3] mt-1.5 flex-shrink-0" />
                    <p className="text-sm">{s}</p>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-7 mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-3">Cost of Root Canal Treatment in India</h2>
                <p className="text-sm leading-relaxed text-gray-600 mb-4">
                  Cost varies based on which tooth is being treated (front teeth have fewer canals than molars and cost less), the clinic's location, and whether a specialist endodontist is performing the procedure.
                </p>
                <ul className="space-y-2">
                  {[
                    'Front teeth (incisors and canines): ₹2,000 – ₹8,000',
                    'Premolars: ₹3,000 – ₹10,000',
                    'Molars (most complex): ₹5,000 – ₹15,000',
                    'Crown (required after treatment): ₹3,000 – ₹20,000 depending on material',
                    'Specialist endodontist fees are typically higher but recommended for complex cases',
                  ].map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#39a5d3] mt-2 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-2 text-[#39a5d3] font-semibold text-sm hover:underline">
                  <ChevronLeft size={16} /> Back to All Blogs
                </Link>
               
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-7">
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Key Facts</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { l: 'Procedure duration', v: '60–90 mins' },
                    { l: 'Appointments needed', v: '1–2 visits' },
                    { l: 'Success rate', v: '85–97%' },
                    { l: 'Tooth lifespan after RCT', v: 'Many years' },
                  ].map((f, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs text-gray-500">{f.l}</p>
                      <p className="text-xs font-bold text-[#39a5d3]">{f.v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">RCT vs Extraction</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { label: 'Keeps natural tooth', rct: 'Yes', ext: 'No' },
                    { label: 'Bone preservation', rct: 'Yes', ext: 'No' },
                    { label: 'Cost', rct: 'Moderate', ext: 'Lower initially' },
                    { label: 'Long-term outcome', rct: 'Better', ext: 'Requires replacement' },
                    { label: 'Recovery', rct: '2–3 days', ext: '3–7 days' },
                  ].map((row, i) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs font-bold text-gray-700 mb-1">{row.label}</p>
                      <div className="flex justify-between">
                        <span className="text-xs text-green-600 font-medium">RCT: {row.rct}</span>
                        <span className="text-xs text-gray-400">Extract: {row.ext}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#19354a] rounded-xl p-6 text-white text-center">
                <h3 className="font-bold mb-2">Book a Dental Consultation</h3>
                <p className="text-white/80 text-xs mb-4">Find verified dentists on Docmize today.</p>
                <Link to="/doctors?specialty=Dentist" className="block bg-white text-[#19354a] font-bold py-2.5 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  Find a Dentist
                </Link>
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs">Call us anytime</p>
                  <a href="tel:+918920845297" className="text-white font-bold text-sm">+91 89208 45297</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogRootCanal;